import { all, call, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import {
    fetchAllApplications,
    fetchAllRoles,
    fetchAuthProviderConfig,
    fetchAuthUser,
    fetchUserById,
    fetchUserMfa,
    requestUserMfaCode,
} from 'services/authService';
import { selectFormById } from 'store/formControlSlice/formControlSelectors';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import * as homeSelectors from 'store/homePageSlice/homePageSelectors';
import { mapUserApplicationsAndRoles } from 'utils/applicationUtils';
import { APPS } from 'utils/constants';
import { sendLocalStorageSignal } from 'utils/sessionStorageHandlers';
import { notificationHandler } from 'utils/utils';
import { authActions } from './authSlice';

import store from '../store';

import { loginRedirectUrlStorage } from 'browserStorage/loginRedirectUrlStorage';
import { axiosSSO } from 'config';

function* loginStart() {
    try {
        const { form } = yield select(selectFormById('login'));
        const {
            errors,
            values: { username: userName, password },
        } = form;

        let error;

        for (let fieldName of Object.keys(errors)) {
            yield put(
                formControlActions.setFieldTouched({
                    formId: 'login',
                    fieldName,
                })
            );
            if (errors[fieldName]) {
                error = true;
            }
        }

        if (error) throw new Error('Error on form inputs');

        let response = yield axiosSSO.post(
            '/login',
            {
                username: userName,
                password,
            },
            { headers: { Authorization: 'Basic c3NvOnNzb1N0YWcxbmch' } }
        );

        if (response.status === 200) {
            const isRedirected = yield call(redirectOnLogin);
            if (!isRedirected) {
                yield put(authActions.loginSuccess());
            }
        }
    } catch (err) {
        if (err.response?.status === 401) {
            yield put(authActions.setFormErrorText('INVALID_CREDENTIALS'));
        }
        yield put(authActions.loginError(err.message));
    }
}

function* loginCheckMfaStart() {
    try {
        const state = store.getState();
        const keycloakEnabled = state?.featureFlag?.keycloakIntegrationEnabled;
        const { form } = yield select(selectFormById('login'));
        const {
            errors,
            values: { username: userName, password },
        } = form;

        let error;
        for (let fieldName of Object.keys(errors)) {
            yield put(
                formControlActions.setFieldTouched({
                    formId: 'login',
                    fieldName,
                })
            );
            if (errors[fieldName]) error = true;
        }
        if (error) throw new Error('Error on form inputs');

        if (!keycloakEnabled) {
            yield put(authActions.loginStart());
            return;
        }

        const mfaResponse = yield call(fetchUserMfa, userName);
        const mfaRequired = mfaResponse?.data?.mfaRequired;
        if (mfaRequired) {
            yield put(authActions.setPendingCredentials({ username: userName, password }));
            yield put(authActions.setMfaRequired(true));
            yield put(authActions.loginCheckMfaSuccess());
            return;
        }

        yield put(authActions.loginStart());
    } catch (err) {
        yield put(authActions.setFormErrorText(err?.message));
        yield put(authActions.loginError(err?.message));
    }
}

function* verifyOtpStart({ payload }) {
    try {
        const state = store.getState();
        const keycloakEnabled = state?.featureFlag?.keycloakIntegrationEnabled;
        if (!keycloakEnabled) {
            // If integration disabled, treat verify as normal login
            yield put(authActions.loginStart());
            return;
        }
        const { otp } = payload;
        const { pendingCredentials } = yield select((state) => state.auth);
        if (!pendingCredentials) throw new Error('Missing pending credentials');
        const { username, password } = pendingCredentials;
        let response = yield axiosSSO.post(
            '/login',
            {
                username,
                password,
                totp: otp,
            },
            { headers: { Authorization: 'Basic c3NvOnNzb1N0YWcxbmch' } }
        );
        if (response.status === 200) {
            const isRedirected = yield call(redirectOnLogin);
            if (!isRedirected) {
                yield put(authActions.loginSuccess());
            }
        }
    } catch (err) {
        yield put(authActions.setFormErrorText('INVALID_CREDENTIALS'));
        yield put(authActions.setMfaRequired(false));
        yield put(authActions.setPendingCredentials(null));
        yield put(formControlActions.setFieldValue({ formId: 'login', fieldName: 'otp', value: '' }));
        yield put(authActions.loginError(err.message));
    }
}

function* resendOtpStart({ payload }) {
    try {
        const { username } = payload || {};
        if (!username) return;
        const state = store.getState();
        const keycloakEnabled = state?.featureFlag?.keycloakIntegrationEnabled;
        if (!keycloakEnabled) return;
        yield call(requestUserMfaCode, username);
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Error sending code',
        });
    }
}

function* ssoLoginSuccess() {
    try {
        const isRedirected = yield call(redirectOnLogin);
        if (!isRedirected) {
            yield put(authActions.loginSuccess());
            window.location.replace('/');
        }
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Error on SSO login success',
        });
    }
}

function* getUserData() {
    try {
        const response = yield call(fetchAuthUser);
        const { username, roles, id: userId } = response?.data;
        const user = { username, roles: roles || [], userId };
        yield put(authActions.setUser(user));
        sessionStorage.setItem('user', JSON.stringify(user));
        sendLocalStorageSignal('UserLoggedIn', user);
        return userId;
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Error getting user data!',
        });
        yield put(authActions.getUserDataError());
    }
}

function* logout() {
    try {
        yield put(authActions.setUser(null));
        sessionStorage.clear();
        sendLocalStorageSignal('UserLoggedOut');
        yield axiosSSO.post('/logout');
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Logout error',
        });
    }
}

function* redirectOnLogin() {
    try {
        // Force redirect if there's inputted redirect url
        // If there's redirect url, redirect to it
        const inputtedRedirectUrl = loginRedirectUrlStorage.read();
        if (inputtedRedirectUrl) {
            // Clear redirect url to ensure it's not used again
            loginRedirectUrlStorage.remove();
            // Redirect to the url
            window.location.replace(inputtedRedirectUrl);
            return true;
        }

        // If there are only two applications, redirect to the other application
        const userId = yield call(getUserData);
        const { user, allRoles, allApps } = yield call(fetchUserDataWithRolesAndApps, userId);
        const applications = mapUserApplicationsAndRoles(user, allRoles, allApps);
        if (applications.length === 2) {
            const application = applications.find((item) => item.app.appName !== APPS.SSO);
            const redirectUrl = application.app?.applicationUrl;
            if (redirectUrl) {
                const finalUrl =
                    redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')
                        ? redirectUrl
                        : `${window.location.protocol}//${redirectUrl}`;

                window.location.replace(finalUrl);

                return true;
            }
        }
    } catch (error) {
        yield call(notificationHandler, {
            err,
            title: 'Error getting user app!',
        });
    }
}

function* changePasswordStarts() {
    try {
        const { form } = yield select(selectFormById('change-password'));
        const {
            errors,
            values: { oldPassword, newPassword },
        } = form;

        let error;

        for (let fieldName of Object.keys(errors)) {
            yield put(
                formControlActions.setFieldTouched({
                    formId: 'change-password',
                    fieldName,
                })
            );
            if (errors[fieldName]) {
                error = true;
            }
        }

        if (error) throw new Error('Error on form inputs');
        const user = yield select(homeSelectors.selectUser);
        const id = user.id.toString();
        const response = yield axiosSSO.post('/change-password', {
            id,
            oldPassword: oldPassword && oldPassword,
            newPassword,
        });

        if (response.status === 200) {
            yield notificationHandler({
                title: `SUCCESSFULLY CHANGED PASSWORD!`,
                variant: 'success',
            });
        }
    } catch (err) {
        if (err.response?.status === 401) {
            yield put(authActions.setFormErrorText('Invalid credentials!'));
        }

        if (err.response?.status === 404) {
            yield put(yield notificationHandler({ title: 'Password does not match' }));
        } else {
            yield put(yield notificationHandler({ title: 'Error changing password' }));
        }
        yield put(authActions.loginError(err.message));
    }
}

function* changeUserPasswordStart({ payload }) {
    try {
        const { form } = yield select(selectFormById('change-user-password'));
        const {
            errors,
            values: { newPassword },
        } = form;

        let error;

        for (let fieldName of Object.keys(errors)) {
            yield put(
                formControlActions.setFieldTouched({
                    formId: 'change-user-password',
                    fieldName,
                })
            );
            if (errors[fieldName]) {
                error = true;
            }
        }

        if (error) throw new Error('Error on form inputs');
        const response = yield axiosSSO.post('/change-password', {
            id: payload.id,
            newPassword,
        });

        if (response.status === 200) {
            yield notificationHandler({
                title: `SUCCESSFULLY CHANGED PASSWORD!`,
                variant: 'success',
            });
        }
    } catch (err) {
        if (err.response?.status === 401) {
            yield put(authActions.setFormErrorText('Invalid credentials!'));
        }
        yield put(authActions.loginError(err.message));
    }
}

function* fetchUserDataWithRolesAndApps(userId) {
    try {
        const [userResponse, rolesResponse, appsResponse] = yield all([call(fetchUserById, userId), call(fetchAllRoles), call(fetchAllApplications)]);

        const user = userResponse.data;
        const allRoles = rolesResponse.data.args;
        const allApps = appsResponse.data;

        return { user, allRoles, allApps };
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Error fetching user, roles, or applications data',
        });
    }
}

function* getAuthProviderConfig() {
    try {
        const response = yield fetchAuthProviderConfig();
        yield put(authActions.setAuthProviderConfig(response?.data));
        return response?.data;
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Error fetching auth provider config',
        });
    }
}

function* onGetUserData() {
    yield takeLatest(authActions.getUserData.type, getUserData);
}

function* onChangeUserPasswordStart() {
    yield takeLatest(authActions.changeUserPasswordStart.type, changeUserPasswordStart);
}

function* onChangePasswordStart() {
    yield takeLatest(authActions.changePasswordStart.type, changePasswordStarts);
}

function* onLoginStart() {
    yield takeLeading(authActions.loginStart.type, loginStart);
}

function* onLoginCheckMfaStart() {
    yield takeLeading(authActions.loginCheckMfaStart.type, loginCheckMfaStart);
}

function* onVerifyOtpStart() {
    yield takeLeading(authActions.verifyOtpStart.type, verifyOtpStart);
}

function* onResendOtpStart() {
    yield takeLeading(authActions.resendOtpStart.type, resendOtpStart);
}

function* onLogoutStart() {
    yield takeLatest(authActions.logoutStart.type, logout);
}

function* onSsoLoginSuccess() {
    yield takeEvery(authActions.ssoLoginSuccess.type, ssoLoginSuccess);
}

function* onGetAuthProviderConfig() {
    yield takeLatest(authActions.getAuthProviderConfig.type, getAuthProviderConfig);
}

export function* authSaga() {
    yield all([
        call(onLoginStart),
        call(onLoginCheckMfaStart),
        call(onVerifyOtpStart),
        call(onResendOtpStart),
        call(onLogoutStart),
        call(onChangePasswordStart),
        call(onChangeUserPasswordStart),
        call(onGetUserData),
        call(onSsoLoginSuccess),
        call(onGetAuthProviderConfig),
    ]);
}
